import {
  FormattedPlanDetails,
  FormattedPlanDetailsMapping,
  NewPaymentPriceResponse,
  PlanUsageLimits,
  PromoCodePlansPricesType,
  PurchaseSubscriptionType,
  SubscriptionDuration,
  SubscriptionPlanType,
  UsageType,
} from "@shared/types";
import { ValueOf } from "@shared/types/utils";
import { ReactNode } from "react";

export interface SharedPlanCardProps {
  plans: FormattedPlanDetailsMapping;
  plan: FormattedPlanDetails;
  queryDuration: SubscriptionDuration;
  setSubscriptionType: (subscriptionType: PurchaseSubscriptionType) => void;
  isUpgrade?: boolean;
  subscriptionParams: SubscriptionParams;
  promoCode?: string | null;
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
}

export const SUBSCRIPTION_STEPS = {
  PLAN: "plan",
  DURATION: "duration",
} as const;

export type SubscriptionStep = ValueOf<typeof SUBSCRIPTION_STEPS>;

export type SetDuration = (
  subscriptionDuration: SubscriptionDuration,
  step?: SubscriptionStep
) => void;

export interface SubscriptionParams {
  duration: SubscriptionDuration;
  plan?: PurchaseSubscriptionType;
  paymentMethod?: string;
  step?: SubscriptionStep;
  isAllPlans?: boolean;
}

export interface DowngradeQuotaModalProps {
  isOpen?: boolean;
  onCancel: () => void;
  selectedPlanUsageLimits: PlanUsageLimits | undefined;
  currentQuotaUsage: PlanUsageLimits | undefined;
  planType?: SubscriptionPlanType;
}

export interface ConfirmDowngradeModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onOk: () => void;
  content: string[];
}

export interface AutoRenewalModalProps {
  isOpen: boolean;
  closeModal: () => void;
  content: string[];
  footerData: {
    secondaryButton: {
      text: string;
      onClick: () => void;
    };
    primaryButton: {
      text: string;
      onClick: () => void;
    };
  };
}

export interface CancelSubscriptionModalProps {
  visible: boolean;
  closeModal: () => void;
}

export enum BillingTab {
  PLAN_USAGE = "Plan Usage",
  BILLING_HISTORY = "Billing History",
}

export interface SharedStepWithSummaryProps {
  subscriptionParams: SubscriptionParams;
  setDuration: SetDuration;
  plan?: FormattedPlanDetails;
  plans: FormattedPlanDetailsMapping;
  previousStep: () => void;
  setQueryPaymentMethod?: (paymentMethod: string) => void;
  isUpgrade?: boolean;
  subGroupId?: number;
}

export enum PaymentOption {
  PAYPAL = "paypal",
  APPLE_PAY = "apple pay",
}

export interface SharedChooseDurationProps {
  plan: FormattedPlanDetails | undefined;
  duration: SubscriptionDuration;
  setDuration: (duration: SubscriptionDuration) => void;
  back?: () => void;
  plans: FormattedPlanDetailsMapping;
  promoCodePrices?: PromoCodePlansPricesType;
  isUpgrade?: boolean;
}

export interface DurationRadioProps {
  plan?: FormattedPlanDetails;
  duration: SubscriptionDuration;
  selectedDuration: SubscriptionDuration;
  setDuration: (duration: SubscriptionDuration) => void;
  plans: FormattedPlanDetailsMapping;
  promoCode?: NewPaymentPriceResponse;
  isBottomSummary?: boolean;
  hideRadio?: boolean;
}

export enum PaymentStatus {
  PENDING = "pending",
  CANCELLED = "cancelled",
  PAID = "paid",
  EXPIRED = "expired",
}

export type GetQuotaPlanFeatureType = (
  usageType: UsageType | string,
  t: (key: any, options?: any) => string,
  numItemsText?: string,
  titleObjectName?: string,
  showAdditionalQuota?: boolean,
  extraQuota?: number,
  planType?: SubscriptionPlanType
) => ReactNode;

export enum SubscriptionPaymentMethod {
  PAYPAL = "PayPal",
  CREDIT_CARD = "Credit Card",
  CRYPTO = "Crypto",
}

export enum CancellationStep {
  MISSING_FEATURES,
  LOSE_DISCOUNT,
  REASON,
  ARE_YOU_SURE,
  SUCCESS,
}
